import { h, Fragment } from "preact";
import { useEffect, useRef, useState } from "react";
import { Button, Select, Typography } from "antd";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { ServiceCard } from "./ServiceCard";
import { VlinkModal } from "./VlinkModal";
import axios from "axios";
import useFlowTemplates from "./useFlowTemplates.js";
import FlowSettings from "./FlowSettings.jsx";
import { useFlowServices } from "./useFlowServices.js";
import { collectData } from "./collectFlowData.js";
import { activateTemplateServices } from "./activateTemplateServices.js";
import { ServiceCardContent } from "./ServiceCardContent.jsx";

const { Option } = Select;

const CreateFlowTab = ({ templateFromParent, onTemplateLoaded }) => {
  const { templates, isTemplatesLoading } = useFlowTemplates();

  const [formData, setFormData] = useState({
    personId: "",
    IIN: "",
    fullName: "",
    flowLogo: "",
    phoneNumber: "",
    selectedLanguage: "ru",
    callbackUrl: "",
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [vlink, setVlink] = useState("");
  const [error, setError] = useState("");
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const verifyDebounceRef = useRef(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [esignFileList, setEsignFileList] = useState([]);

  const {
    services,
    setServices,
    enabledServices,
    setEnabledServices,
    movingServiceId,
    direction,
    clearServices,
    toggleService,
    onDragEnd,
    moveService,
    activeServiceNumbers,
    updateInputParam,
    updateCheckbox,
    updateSelectParam,
    updateRadioGroup,
    updateChildCheckbox,
    updateCheckboxNested,
    updateSelectParamNested,
  } = useFlowServices(formData.selectedLanguage);

  useEffect(() => {
    if (templateFromParent) loadTemplate(templateFromParent);
  }, [templateFromParent]);

  const clearAll = () => {
    clearServices();
    setSelectedTemplate(null);
    setFormData((prev) => ({ ...prev, phoneNumber: "", IIN: "", fullName: "", personId: "" }));
    setFileList([]);
    setError("");
  };

  const loadTemplate = (template) => {
    if (!template) return;
    clearAll();
    const { updatedServices, servicesToEnable } = activateTemplateServices(template.template);
    setServices(updatedServices);
    setEnabledServices(servicesToEnable);
    setSelectedTemplate({
      value: template.template_id,
      label: `${template.template_name} (${template.template_id})`,
    });
    if (onTemplateLoaded) onTemplateLoaded();
  };

  const uploadFileData = async (file, uid, service) => {
    setIsLoading(true);
    const form = new FormData();
    form.set("upload_file", file, file.name);
    form.set("person_id", formData.personId);
    form.set("label", "doc_preview");
    try {
      const res = await axios.post("/user/resources/storage", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (service === "doc-preview") {
        setFileList((prev) => [...prev, { name: file.name, sessionId: res.data.session_id, uid }]);
      } else if (service === "esign-sign") {
        setEsignFileList((prev) => [...prev, { name: file.name, sessionId: res.data.session_id, uid }]);
      }
    } catch (err) {
      setError(err?.response?.data.error_msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileRemove = (uid, listType) => {
    if (listType === "doc-preview") setFileList((prev) => prev.filter((f) => f.uid !== uid));
    else if (listType === "esign-sign") setEsignFileList((prev) => prev.filter((f) => f.uid !== uid));
  };

  const buildPostData = () => {
    const postData = { status_page: true, pipeline: [] };
    collectData(services, postData, {
      lang: formData.selectedLanguage,
      logoUrl: formData.flowLogo,
      fileList,
      esignFileList,
    });
    return postData;
  };

  const resolveCallbackUrl = () => {
    if (!formData.callbackUrl) return "https://verigram.ai";
    return /^https?:\/\//i.test(formData.callbackUrl)
      ? formData.callbackUrl
      : `https://${formData.callbackUrl}`;
  };

  const prepareFlowData = () => ({
    callback_url: resolveCallbackUrl(),
    person_id: formData.personId,
    locale: formData.selectedLanguage,
    template: buildPostData(),
    input_data: {
      ...(formData.phoneNumber.length > 0 && { phone_number: formData.phoneNumber }),
      ...(formData.IIN.length > 0 && { iin: formData.IIN }),
      ...(formData.fullName.length > 0 && { full_name: formData.fullName }),
      ...((fileList.length > 0 || esignFileList.length > 0) && {
        files: {
          doc_preview: fileList.map((f) => ({ filename: f.name, file_id: f.sessionId })),
          esign: esignFileList.map((f) => ({ filename: f.name, file_id: f.sessionId })),
        },
      }),
    },
  });

  const verifyData = async () => {
    if (enabledServices.length === 0) return;
    setError("");
    setIsLoading(true);
    await axios.post("/user/flow/verify", prepareFlowData()).catch((err) => {
      setError(err?.response?.data.error_msg);
    });
    setIsLoading(false);
  };

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
      return;
    }
    clearTimeout(verifyDebounceRef.current);
    verifyDebounceRef.current = setTimeout(() => {
      verifyData();
    }, 500);
    return () => clearTimeout(verifyDebounceRef.current);
  }, [enabledServices, formData, fileList, esignFileList]);

  const sendRequest = async () => {
    setError("");
    await axios
      .post("/user/flow", prepareFlowData())
      .then((res) => {
        setIsModalOpen(true);
        setVlink(res.data.vlink);
        clearAll();
      })
      .catch((err) => {
        setError(err?.response?.data.error_msg);
      });
  };

  const renderCardContent = (service) => (
    <ServiceCardContent
      service={service}
      setServices={setServices}
      onUpdateInput={updateInputParam}
      onUpdateCheckbox={updateCheckbox}
      onUpdateSelect={updateSelectParam}
      onUpdateRadioGroup={updateRadioGroup}
      onUpdateChildCheckbox={updateChildCheckbox}
      onCheckboxNested={updateCheckboxNested}
      onSelectNested={updateSelectParamNested}
      onUploadFile={uploadFileData}
      onRemoveFile={handleFileRemove}
      personId={formData.personId}
    />
  );

  return (
    <>
      <div style={{ display: "flex", gap: "20px", flexDirection: "column", background: "white" }}>
        <div className="flows-tab-content">
          <div className="flows-two-col">
            <div style={{ flex: 1 }}>
              <h3>Create Flow</h3>
              <Typography.Text>
                <p>
                  Create your own verification method by combining Verigram services into one
                  sequence - Flow. Switch on the modules you need and drag & drop them into the
                  desired order.
                </p>
              </Typography.Text>

              <div style={{ marginBottom: "20px" }}>
                <h3>Load Template</h3>
                <Select
                  placeholder="Select a template..."
                  style={{ width: "100%" }}
                  value={selectedTemplate}
                  labelInValue
                  onChange={(option) => {
                    const tmpl = templates.find((t) => t.template_id === option.value);
                    loadTemplate(tmpl);
                  }}
                  allowClear
                  onClear={clearAll}
                  loading={isTemplatesLoading}
                >
                  {templates?.map((t) => (
                    <Option key={t.template_id} value={t.template_id}>
                      {t.template_name}{" "}
                      <span style={{ color: "#999", fontSize: 12 }}>({t.template_id})</span>
                    </Option>
                  ))}
                </Select>
              </div>

              <h3>Services</h3>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="services">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {services.map((service, index) => {
                        const activeServiceNumber = activeServiceNumbers.find(
                          (n) => n.id === service.id
                        )?.number;
                        return (
                          <Draggable
                            key={service.id}
                            draggableId={String(service.id)}
                            index={index}
                            isDragDisabled={service.paramName === "consent"}
                            isDropDisabled={service.paramName === "consent"}
                          >
                            {(provided) => (
                              <div ref={provided.innerRef} {...provided.draggableProps}>
                                <ServiceCard
                                  service={service}
                                  services={services}
                                  moveService={moveService}
                                  toggleService={toggleService}
                                  renderCardContent={renderCardContent}
                                  provided={provided}
                                  index={index}
                                  activeServiceNumber={activeServiceNumber}
                                  movingServiceId={movingServiceId}
                                  direction={direction}
                                />
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>

              {error && (
                <div style={{ paddingTop: "30px" }}>
                  <span style={{ color: "red" }}>{error}</span>
                </div>
              )}
              <div style={{ paddingTop: "30px" }}>
                <Button
                  disabled={enabledServices.length <= 0 || error?.length > 0 || isLoading}
                  onClick={sendRequest}
                >
                  Create flow
                </Button>
                <Button style={{ marginLeft: "10px" }} onClick={clearAll}>
                  Clear all
                </Button>
              </div>
            </div>

            <div className="flow-settings" style={{ flex: 1 }}>
              <FlowSettings formData={formData} setFormData={setFormData} />
            </div>
          </div>
        </div>

        {isModalOpen && (
          <VlinkModal
            vlink={vlink}
            setIsModalOpen={setIsModalOpen}
            isModalOpen={isModalOpen}
            setVlink={setVlink}
            mode="flow"
          />
        )}
      </div>
    </>
  );
};

export default CreateFlowTab;
